import { getBaseRole } from '@/helpers/customRoles';
import { Dispatch, FC, SetStateAction, useEffect, useState } from 'react'
import cn from 'classnames'
import mclasses from '@pages/Admin/CRM/styles/modal.module.css'
import iclasses from '@pages/Admin/CRM/styles/input.module.css'
import cclasses from '@pages/Admin/CRM/styles/checkbox.module.css'
import classes from './index.module.css'
import { GeneralCourseInformation, RefetchGeneralDataVariantType } from '../..'
import { ElClassFormDataType } from '../PageContent/PageContent'
import { Checkbox, Segmented, Select, Tooltip } from 'antd'
import { InfoCircleOutlined } from '@ant-design/icons'
import { InputNew } from '@UI/InputNew'
import Button from '@UI/New/Button'
import { classApi } from '@/api'
import { isError } from '@/helpers/checkQuery'
import { useAddAlert } from '@/hooks/useAddAlert'
import TooltipWrapper from '@UI/New/TooltipWrapper/TooltipWrapper'
import ClassDirectLink from './components/ClassDirectLink/ClassDirectLink'
import ClassAutoChats from './components/ClassAutoChats/ClassAutoChats'
import { ClassInfoType } from '../../types'
import {
  getDefaultPaymentAcquiringId,
  getPaymentAcquiringsWithLegacyFallback,
  getPaymentAcquiringOptions,
  MULTIPLE_PAYMENT_ACQUIRINGS_TOOLTIP,
  NO_PAYMENT_ACQUIRING_ID,
} from '@/helpers/paymentAcquirings'
import useFranchises from '@/hooks/useFranchises'
import { useSelector } from 'react-redux'
import { selectProfile } from '@/store/profile.reducer'

interface ClassSettingsProps {
  // otherUnsavedChanges
  generalData: GeneralCourseInformation
  elClassFormData: ElClassFormDataType
  setElClassFormData: Dispatch<SetStateAction<ElClassFormDataType>>
  refetchGeneralData: (variant: RefetchGeneralDataVariantType) => void

  setOtherUnsavedChanges: Dispatch<SetStateAction<UnsavedChanges>>
}

export type UnsavedChanges = { type: 'vks' | 'price'; message: string }[]

const ClassSettings: FC<ClassSettingsProps> = ({
  generalData,
  elClassFormData,
  setElClassFormData,
  refetchGeneralData,
  setOtherUnsavedChanges,
}) => {
  const addAlert = useAddAlert()
  const profile = useSelector(selectProfile)
  const isFranchiseManager = getBaseRole(profile?.role) === 'franchise-manager'
  const paymentSettings = useSelector(
    (store: any) => store.settings.paymentSystem,
  )
  const paymentAcquirings = getPaymentAcquiringsWithLegacyFallback({
    paymentAcquirings: [paymentSettings?.paymentAcquirings],
    legacyPaymentSystem: paymentSettings?.paymentSystem,
  })
  const paymentAcquiringOptions = getPaymentAcquiringOptions(paymentAcquirings)
  const defaultPaymentAcquiringId =
    getDefaultPaymentAcquiringId(paymentAcquirings)
  const {
    enabled: franchisesEnabled,
    options: franchiseOptions,
    loading: franchisesLoading,
  } =
    useFranchises()

  const [linkVKS, setLinkVKS] = useState({
    value: elClassFormData.link_vks ?? null,
    error: false,
  })

  const [amountState, setAmountState] = useState({
    visibleButton: false,
    value: elClassFormData.amount ?? 100,
    error: false,
  })

  useEffect(() => {
    const arr = []
    if (
      elClassFormData.change_vks_link_for_everyone &&
      linkVKS.value !== elClassFormData.link_vks
    ) {
      arr.push({
        type: 'vks',
        message: 'Ссылка на ВКС',
      } as const)
    }

    setOtherUnsavedChanges(arr)
  }, [linkVKS, elClassFormData])

  const renewToFirstDayTooltip =
    'Если у учащегося класс оплачен не до первого числа какого-либо месяца, то при продлении такого обучения система продлит его до первого числа следующего месяца, при этом автоматически пересчитает стоимость этого неполного месяца.'
  const catalogToFirstDayTooltip =
    'Если участник оплачивает данный класс через каталог курсов или по прямой ссылке, он может записаться на него и оплатить обучение только до 1-го числа любого месяца. Соответственно, первый неполный месяц будет перерассчитан в соответствии со стандартной ценой класса.'

  useEffect(() => {
    if (
      !franchisesEnabled ||
      !isFranchiseManager ||
      franchisesLoading ||
      !franchiseOptions.length
    ) {
      return
    }

    const availableIds = franchiseOptions.map((option) => Number(option.value))
    const currentFranchiseId = Number(elClassFormData.franchise_id)
    if (availableIds.includes(currentFranchiseId)) {
      return
    }

    setElClassFormData((prev) => ({
      ...prev,
      franchise_id: availableIds[0],
    }))
  }, [
    franchisesEnabled,
    isFranchiseManager,
    franchisesLoading,
    franchiseOptions,
    elClassFormData.franchise_id,
    setElClassFormData,
  ])

  return (
    <>
      <div className={classes.mainContainer}>
      <div className={classes.verticalList}>
        {franchisesEnabled && (
          <div>
            <p className={mclasses.label}>Филиал</p>
            <Select
              style={{ width: '100%', maxWidth: 420 }}
              value={elClassFormData.franchise_id ?? undefined}
              onChange={(franchise_id) =>
                setElClassFormData((prev) => ({ ...prev, franchise_id }))
              }
              options={franchiseOptions}
              placeholder="Выберите филиал"
              showSearch
              optionFilterProp="label"
              loading={franchisesLoading}
              disabled={isFranchiseManager && franchiseOptions.length <= 1}
            />
          </div>
        )}
        <Checkbox
          className={cn(cclasses.checkbox, classes.checkbox)}
          checked={elClassFormData.one_pay ?? false}
          onChange={(e) => {
            setElClassFormData((prev) => ({
              ...prev,
              one_pay: e.target.checked,
              amount: e.target.checked ? 100 : prev.amount,
              payment_acquiring_id: e.target.checked
                ? prev.payment_acquiring_id ?? defaultPaymentAcquiringId
                : prev.payment_acquiring_id,
              payment_acquiring_id_secondary: e.target.checked
                ? prev.payment_acquiring_id_secondary ?? null
                : prev.payment_acquiring_id_secondary,
            }))
          }}
        >
          Данный класс/обр. траектория платный
          <div className={classes.tooltipWrapper}>
            <TooltipWrapper
              title={
                'При включении данного чек-бокса, когда класс будет добавляться учащимся оплата за все входящие в класс курсы будет происходить по цене, которую вы укажите ниже. Если данный чек-бокс будет выключен, то производить оплату учащемуся нужно будет отдельно за каждый входящий в класс курс'
              }
              className={classes.tooltipMain}
              overlayClassName={classes.tooltipOverlay}
            >
              <InfoCircleOutlined
                className={classes.tooltipIcon}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </TooltipWrapper>
          </div>
        </Checkbox>
        {elClassFormData.one_pay && (
          <div>
            <p className={mclasses.label}>Сумма оплаты/ {!!elClassFormData.access_by_days ? 'N дней,' : 'мес,'} ₽</p>
            <div className={classes.horizontalListSmall}>
              {/* @ts-ignore */}
              <InputNew
                className={cn(iclasses.newInputDesign, classes.inputField)}
                placeholder={'Цена в рублях'}
                value={amountState.value ?? elClassFormData.amount ?? 100}
                onChange={(e: any) => {
                  setElClassFormData((prev) => ({
                    ...prev,
                    one_pay: true,
                    amount: Number(e.target.value),
                  }))

                  const isError = Number(e.target.value) < 1

                  if (isError) {
                    setAmountState((prev) => ({
                      visibleButton: false,
                      value: e.target.value,
                      error: isError,
                    }))
                    return
                  }

                  setAmountState((prev) => ({
                    visibleButton: e.target.value !== elClassFormData.amount,
                    value: e.target.value,
                    error: false,
                  }))
                }}
                validate={amountState.error}
                errorText={'Введите корректную сумму'}
                typeValid="custom"
                type="number"
              />
              {amountState.visibleButton && (
                <Button
                  color="secondary"
                  onClick={() => {
                    classApi
                      .massPaymentAmountUpdate({
                        amount: elClassFormData.amount,
                        class_id: elClassFormData.id,
                      })
                      .then((res) => {
                        if (isError(res)) throw new Error()

                        setAmountState((prev) => ({
                          ...prev,
                          visibleButton: false,
                        }))

                        addAlert({
                          title: 'Успешно',
                          text: 'Цена изменена у всех',
                          type: 'success',
                        })
                      })
                      .catch(() => {
                        addAlert({
                          title: 'Ошибка',
                          text: 'Не удалось изменить цену у всех',
                          type: 'error',
                        })
                      })
                  }}
                >
                  {'Изменить цену \nу всех'}
                </Button>
              )}
            </div>
            <div style={{ marginTop: '10px' }}>
              <p className={mclasses.label}>
                Первый эквайринг для оплаты
                <Tooltip title={MULTIPLE_PAYMENT_ACQUIRINGS_TOOLTIP}>
                  <InfoCircleOutlined
                    className={classes.acquiringTooltip}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </Tooltip>
              </p>
              <Select
                style={{ width: '100%', maxWidth: 420 }}
                value={elClassFormData.payment_acquiring_id ?? null}
                onChange={(value) =>
                  setElClassFormData((prev) => ({
                    ...prev,
                    payment_acquiring_id: value ?? null,
                  }))
                }
                options={paymentAcquiringOptions}
                placeholder="Выберите эквайринг для оплаты"
                notFoundContent={
                  'Ни один эквайринг не подключен. Подключите эквайринг в разделе «Настройки платформы» – «Интеграции»'
                }
                disabled={!paymentAcquiringOptions.length}
                allowClear
                showSearch
              />
            </div>
            <div style={{ marginTop: '10px' }}>
              <p className={mclasses.label}>
                Второй эквайринг для оплаты
                <Tooltip title={MULTIPLE_PAYMENT_ACQUIRINGS_TOOLTIP}>
                  <InfoCircleOutlined
                    className={classes.acquiringTooltip}
                    onPointerEnterCapture={undefined}
                    onPointerLeaveCapture={undefined}
                  />
                </Tooltip>
              </p>
              <Select
                style={{ width: '100%', maxWidth: 420 }}
                value={elClassFormData.payment_acquiring_id_secondary ?? null}
                onChange={(value) =>
                  setElClassFormData((prev) => ({
                    ...prev,
                    payment_acquiring_id_secondary: value ?? null,
                  }))
                }
                options={paymentAcquiringOptions.filter(
                  (option) =>
                    option.value === NO_PAYMENT_ACQUIRING_ID ||
                    option.value !== elClassFormData.payment_acquiring_id,
                )}
                placeholder="Выберите второй эквайринг для оплаты"
                notFoundContent={
                  'Подключите второй эквайринг в разделе «Настройки платформы» – «Интеграции»'
                }
                disabled={!paymentAcquiringOptions.length}
                allowClear
                showSearch
              />
            </div>
            {
              !!elClassFormData.access_by_days && (
                <>
                  {/* @ts-ignore */}
                  <InputNew
                      style={{marginTop: '10px'}}
                      className={cn(iclasses.newInputDesign, classes.inputField)}
                      placeholder={'Количество дней'}
                      value={elClassFormData.custom_payment_period ?? ''}
                      onChange={(e: any) => {
                        const value = e.target.value.replace(/[^0-9]/g, "");
                        setElClassFormData((prev) => ({
                          ...prev,
                          one_pay: true,
                          custom_payment_period: value ? Number(value) : '',
                        }))
                      }}
                      validate={amountState.error}
                      type="number"
                  />
                </>
              )
            }
            {!elClassFormData.access_by_days && (
              <>
                <Checkbox
                  style={{ marginTop: '10px' }}
                  className={cn(cclasses.checkbox, classes.checkbox)}
                  checked={elClassFormData.renew_to_first_day_of_month ?? false}
                  onChange={(e) => {
                    setElClassFormData((prev) => ({
                      ...prev,
                      renew_to_first_day_of_month: e.target.checked,
                    }))
                  }}
                >
                  Учащийся может продлевать класс только до 1 числа каждого месяца (с автоперерасчетом)
                  <div className={classes.tooltipWrapper}>
                    <TooltipWrapper
                      title={renewToFirstDayTooltip}
                      className={classes.tooltipMain}
                      overlayClassName={classes.tooltipOverlay}
                    >
                      <InfoCircleOutlined
                        className={classes.tooltipIcon}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    </TooltipWrapper>
                  </div>
                </Checkbox>
                <Checkbox
                  style={{ marginTop: '10px' }}
                  className={cn(cclasses.checkbox, classes.checkbox)}
                  checked={elClassFormData.catalog_to_first_day_of_month ?? false}
                  onChange={(e) => {
                    setElClassFormData((prev) => ({
                      ...prev,
                      catalog_to_first_day_of_month: e.target.checked,
                    }))
                  }}
                >
                  Первичная оплата класса через каталог или по прямой ссылке только до первого числа любого месяца
                  <div className={classes.tooltipWrapper}>
                    <TooltipWrapper
                      title={catalogToFirstDayTooltip}
                      className={classes.tooltipMain}
                      overlayClassName={classes.tooltipOverlay}
                    >
                      <InfoCircleOutlined
                        className={classes.tooltipIcon}
                        onPointerEnterCapture={undefined}
                        onPointerLeaveCapture={undefined}
                      />
                    </TooltipWrapper>
                  </div>
                </Checkbox>
              </>
            )}
            
            <Checkbox
              style={{marginTop: '10px'}}
              className={cn(cclasses.checkbox, classes.checkbox)}
              checked={elClassFormData.access_by_days ?? false}
              onChange={(e) => {
                setElClassFormData((prev) => ({
                  ...prev,
                  access_by_days: e.target.checked,
                  renew_to_first_day_of_month: e.target.checked
                    ? false
                    : prev.renew_to_first_day_of_month,
                  catalog_to_first_day_of_month: e.target.checked
                    ? false
                    : prev.catalog_to_first_day_of_month,
                }))
              }}
            >
              Продление доступа к классу не по месяцам, а на Н дней
            </Checkbox>
          </div>
        )}
        <Checkbox
          className={cn(cclasses.checkbox, classes.checkbox)}
          checked={!elClassFormData.ignore_payment}
          onChange={(e) => {
            setElClassFormData({
              ...elClassFormData,
              ignore_payment: !e.target.checked,
            })
          }}
        >
          Блокировать доступ к классу/обр.Траектории неоплаченным пользователям
        </Checkbox>

        <Checkbox
          style={{marginTop: '10px'}}
          className={cn(cclasses.checkbox, classes.checkbox)}
          checked={elClassFormData.disable_promo_code_when_extend ?? false}
          onChange={(e) => {
            setElClassFormData((prev) => ({
              ...prev,
              disable_promo_code_when_extend: e.target.checked
            }))
          }}
        >
          Запретить использовать промокод у этого класса при продлении обучения
        </Checkbox>
        <Checkbox
          className={cn(cclasses.checkbox, classes.checkbox)}
          checked={
            elClassFormData.auto_access_duration === null ||
            elClassFormData.auto_access_duration === undefined
              ? false
              : true
          }
          onChange={(e) => {
            setElClassFormData({
              ...elClassFormData,
              auto_access_duration: e.target.checked ? 5 : null,
            })
          }}
        >
          Автоматически давать доступ на N месяцев или дней{' '}
          <div className={classes.tooltipWrapper}>
            <TooltipWrapper
              title={
                'При включении данного чек-бокса всем пользователям, кому добавлен данный класс будет выдан доступ на указанное Вами кол-во месяцев автоматически, без необходимости продления'
              }
              className={classes.tooltipMain}
              overlayClassName={classes.tooltipOverlay}
            >
              <InfoCircleOutlined
                className={classes.tooltipIcon}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
              />
            </TooltipWrapper>
          </div>
        </Checkbox>
        {elClassFormData.auto_access_duration !== null &&
          elClassFormData.auto_access_duration !== undefined && (
            <div>
              <div>
                <p className={mclasses.label}>
                  Кол-во{' '}
                  {elClassFormData.auto_access_unit === 'days'
                    ? 'дней'
                    : 'месяцев'}
                </p>
                {/* @ts-ignore */}
                <InputNew
                  className={cn(iclasses.newInputDesign, classes.inputField)}
                  placeholder={`Кол-во ${
                    elClassFormData.auto_access_unit === 'days'
                      ? 'дней'
                      : 'месяцев'
                  }`}
                  value={elClassFormData.auto_access_duration ?? 0}
                  onChange={(e: any) =>
                    setElClassFormData({
                      ...elClassFormData,
                      auto_access_duration: e.currentTarget.value,
                    })
                  }
                  validate={
                    !(Number(elClassFormData?.auto_access_duration) > 0)
                  }
                  errorText={`Введите корректное кол-во ${
                    elClassFormData.auto_access_unit === 'days'
                      ? 'дней'
                      : 'месяцев'
                  }`}
                  typeValid="custom"
                  type="number"
                />
              </div>
              <Segmented
                value={
                  elClassFormData.auto_access_unit === 'days'
                    ? 'days'
                    : 'months'
                }
                className={classes.segmented}
                onChange={(key) => {
                  setElClassFormData((prev) => ({
                    ...prev,
                    auto_access_unit: key as 'months' | 'days',
                  }))
                }}
                options={[
                  {
                    label: 'Месяцев',
                    value: 'months',
                  },
                  {
                    label: 'Дней',
                    value: 'days',
                  },
                ]}
                onPointerEnterCapture={undefined}
                onPointerLeaveCapture={undefined}
                onResize={undefined}
                onResizeCapture={undefined}
              />
            </div>
          )}
        <ClassDirectLink
          classSettings={elClassFormData as ClassInfoType}
          onLinkShowChange={() => {
            setElClassFormData((prev) => ({
              ...prev,
              direct_link_enabled: !elClassFormData?.direct_link_enabled,
            }))
          }}
        />
        <div>
          <p className={mclasses.label}>Тег класса</p>
          {/* @ts-ignore */}
          <InputNew
            className={cn(iclasses.newInputDesign, classes.inputField)}
            placeholder={'тег'}
            value={elClassFormData.tag ?? ''}
            onChange={(e: any) =>
              setElClassFormData({
                ...elClassFormData,
                tag: e.currentTarget.value,
              })
            }
            type="string"
          />
        </div>
      </div>
      <div className={classes.verticalList}>
        <Checkbox
          className={cn(cclasses.checkbox, classes.checkbox)}
          checked={elClassFormData.mass_add_group ?? false}
          onChange={(e) => {
            setElClassFormData({
              ...elClassFormData,
              mass_add_group: e.target.checked,
            })
          }}
        >
          Автоматически добавлять новые курсы всем учащимся в
          классе/обр.Траектории
        </Checkbox>
        <ClassAutoChats classId={elClassFormData.id ?? null} />
        <Checkbox
          className={cn(cclasses.checkbox, classes.checkbox)}
          checked={
            elClassFormData.pointer === null ||
            elClassFormData.pointer === undefined
              ? false
              : true
          }
          onChange={(e) => {
            setElClassFormData({
              ...elClassFormData,
              pointer: e.target.checked ? '' : null,
            })
          }}
        >
          Включить указатель на класс/обр. тракеторию
        </Checkbox>
        {elClassFormData.pointer !== null &&
          elClassFormData.pointer !== undefined && (
            <div>
              <p className={mclasses.label}>Впишите указатель</p>
              {/* @ts-ignore */}
              <InputNew
                className={cn(iclasses.newInputDesign, classes.inputField)}
                placeholder={'Указатель'}
                value={elClassFormData.pointer}
                onChange={(e: any) =>
                  setElClassFormData({
                    ...elClassFormData,
                    pointer: e.currentTarget.value,
                  })
                }
                validate={(elClassFormData?.pointer ?? '').length < 1}
                errorText={'Поле не может быть пустым'}
                typeValid="custom"
              />
            </div>
          )}
        <Checkbox
          className={cn(cclasses.checkbox, classes.checkbox)}
          checked={elClassFormData.change_vks_link_for_everyone ?? false}
          onChange={(e) => {
            setElClassFormData((prev) => ({
              ...prev,
              change_vks_link_for_everyone: e.target.checked,
            }))
          }}
        >
          Изменить ссылку на ВКС для всех занятий в классе/обр. тракетории
        </Checkbox>
        {elClassFormData.change_vks_link_for_everyone && (
          <div>
            <p className={mclasses.label}>Введите ссылку</p>
            <div className={classes.horizontalListSmall}>
              {/* @ts-ignore */}
              <InputNew
                className={cn(iclasses.newInputDesign, classes.inputField)}
                placeholder={'Cсылка на ВКС'}
                value={linkVKS.value ?? elClassFormData.link_vks ?? ''}
                onChange={(e: any) => {
                  setLinkVKS((prev) => ({
                    ...prev,
                    value: e.target.value,
                    error: String(e.target.value).length < 1,
                  }))
                }}
                validate={linkVKS.error}
                errorText={'Поле не может быть пустым'}
                typeValid="custom"
              />
              {linkVKS.value !== elClassFormData.link_vks && (
                <Button
                  color="secondary"
                  onClick={() => {
                    if (!linkVKS.value || linkVKS.value.length < 1) {
                      setLinkVKS((prev) => ({ ...prev, error: true }))
                      return
                    }

                    if (linkVKS.value && linkVKS.value.length > 0) {
                      setElClassFormData((prev) => ({
                        ...prev,
                        change_vks_link_for_everyone: true,
                        link_vks: linkVKS.value,
                      }))
                    }
                  }}
                >
                  {'Изменить ссылку \nво всех уроках'}
                </Button>
              )}
            </div>
          </div>
        )}
        </div>
      </div>
    </>
  )
}

export default ClassSettings
